import React, { useEffect, useRef, useState } from "react";
import {
  useForm,
  FormProvider,
  useFormContext,
  useFormState,
  useWatch,
} from "react-hook-form";
import CustomSelect from "@pages/Template/components/CustomSelect";
import CountryDropdown from "@shared/components/CountryDropdown";
import { companyService } from "@services/CompanyService";
import Loader from "@shared/components/Loader";

interface FormValues {
  company_role_id: string;
  countryName: string;
  country_id: number;
  focus_area_id: number;
  website: string;
  staff_number: string;
  needs: string[];
  other: string;
  needs_other_guard?: string;
}

interface Props {
  onSubmit: (data: any) => void;
  companyName: string;
  goBack: () => void;
  isLoading: boolean;
  defaultValues?: any;
  onChange?: (data: any) => void;
}

const staffOptions = ["1-5", "6-12", "21-50", "51-100", "100+"];

const needsOptions = [
  "Speed up our reporting process",
  "Help manage projects and programs",
  "Simplify donor reporting and compliance",
  "Improve visibility across teams and field offices",
  "Gain insights through dashboards and analytics",
  "Streamline team collaboration",
  "Track impact and outcomes",
  "Centralize data and documentation",
  "Automate repetitive tasks (like reminders, status updates)",
  "Reduce time spent on manual data entry",
];

const Checkbox = ({
  label,
  value,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  value: string;
  checked: boolean;
  onChange: React.ChangeEventHandler<HTMLInputElement> | undefined;
  disabled?: boolean;
}) => (
  <label
    className={`flex items-center gap-2 cursor-pointer w-full ${
      disabled ? "opacity-50" : ""
    }`}
  >
    <input
      type="checkbox"
      value={value}
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      className="appearance-none mt-1 w-5 h-5 border-2 cursor-pointer border-blue-500 rounded checked:bg-blue-500 checked:border-blue-500 focus:outline-none transition-all duration-200 shrink-0"
    />
    <span className="text-gray-800 font-medium text-sm mt-1.5">{label}</span>
  </label>
);

const SectionCard: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="border border-gray-200 rounded-md p-6 space-y-4 bg-[#FFFFFE]">
    <h3 className="text-md font-semibold text-gray-900 text-sm 2xl:text-base">
      {title}
    </h3>
    {children}
  </div>
);

const InnerForm: React.FC<{
  onSubmit: (data: FormValues) => void;
  isLoading: boolean;
}> = ({ onSubmit, isLoading }) => {
  const {
    register,
    watch,
    setValue,
    handleSubmit,
    getValues,
    setError,
    clearErrors,
    trigger,
  } = useFormContext<FormValues>();
  const { errors, isValid } = useFormState<FormValues>();

  const selectedNeeds = watch("needs") || [];
  const otherValue = watch("other") || "";

  const selectedCountryId = useWatch({ name: "country_id" });
  const selectedCountryName = useWatch({ name: "countryName" });

  const [roleOptions, setRoleOptions] = useState<any>([]);
  const [focusAreaOptions, setFocusAreaOptions] = useState<any>([]);

  useEffect(() => {
    (async () => {
      try {
        const focusAreas = await companyService.getCompanyFocusAreas();
        setFocusAreaOptions(
          focusAreas.map((area: any) => ({
            label: area.name,
            value: area.id,
          }))
        );
      } catch (error) {
        console.error("Error fetching company focus area:", error);
      }

      try {
        const roles = await companyService.getCompanyRoles();
        setRoleOptions(
          roles.map((role: any) => ({
            label: role.name,
            value: role.id,
          }))
        );
      } catch (error) {
        console.error("Error fetching company roles:", error);
      }
    })();
  }, []);

  useEffect(() => {
    register("company_role_id", { required: "Role is required" });
    register("focus_area_id", { required: "Focus area is required" });
    register("website", { required: "Website is required" });
    register("staff_number", { required: "Staff size is required" });
    register("countryName", { required: "Country is required" });
  }, [register]);

  const submitHandler = (data: FormValues) => {
    const parts: string[] = [];

    if (selectedNeeds.length > 0) parts.push(...selectedNeeds);
    if (otherValue.trim().length > 0) parts.push(otherValue.trim());

    const registration_target = parts.join(", ");

    const payload = {
      ...data,
      registration_target,
    };

    onSubmit(payload);
  };

  useEffect(() => {
    register("needs_other_guard", {
      validate: () => {
        const needs = getValues("needs") || [];
        const other = (getValues("other") || "").trim();

        if (other && other.length < 10) {
          return "Other must be at least 10 characters long.";
        }
        if (needs.length === 0 && !other) {
          return "Please select at least one need or specify Other.";
        }
        return true;
      },
    });
  }, [register, getValues]);

  return (
    <form
      onSubmit={handleSubmit(submitHandler)}
      className="space-y-8 bg-[#F9FAFB] w-full"
    >
      <SectionCard title="Personal Information">
        <div>
          <label className="block mb-1 text-sm 2xl:text-base">
            Role <span className="text-red-500">*</span>
          </label>
          <CustomSelect
            name="company_role_id"
            options={roleOptions}
            placeholder="Select your role"
            useFormContext={useFormContext()}
          />
          {errors.company_role_id && (
            <p className="text-red-500 text-sm mt-1">
              {String(errors.company_role_id.message)}
            </p>
          )}
        </div>
      </SectionCard>

      <SectionCard title="About Your Organization">
        <div className="w-full grid grid-cols-1 sm:grid-cols-2  sm:space-x-4">
          <div className="mb-4 w-full">
            <label className="block mb-1 text-sm 2xl:text-base">
              Country <span className="text-red-500">*</span>
            </label>
            <CountryDropdown
              {...register("countryName", {
                required: "Country is required",
              })}
              onSelect={(id: number, name: string) => {
                setValue("country_id", id, { shouldDirty: true });
                setValue("countryName", name, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
              }}
              selectedCountry={{
                id: selectedCountryId,
                name: selectedCountryName,
              }}
              isEditing={true}
              customInputStyle="h-10 2xl:h-12"
            />
            {errors.countryName && (
              <p className="text-red-500 text-sm mt-1">
                {String(errors.countryName.message)}
              </p>
            )}
          </div>

          <div className="mb-4 w-full">
            <label className="block mb-1 text-sm 2xl:text-base">
              Focus Area <span className="text-red-500">*</span>
            </label>
            <CustomSelect
              name="focus_area_id"
              options={focusAreaOptions}
              placeholder="Select your focus area"
              useFormContext={useFormContext()}
            />
            {errors.focus_area_id && (
              <p className="text-red-500 text-sm mt-1">
                {String(errors.focus_area_id.message)}
              </p>
            )}
          </div>
        </div>

        <div className="mb-4">
          <label className="block mb-1 text-sm 2xl:text-base">
            Organization Website <span className="text-red-500">*</span>
          </label>
          <input
            {...register("website", {
              required: "Website is required",
              pattern: {
                value:
                  /^(?:https?:\/\/|www\.)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+(?:\/[^\s]*)?$/i,
                message: "Please enter a valid website URL.",
              },
            })}
            placeholder="www.your-organization.com"
            className="w-full border-gray-300 border px-3 py-2 rounded text-xs sm:text-sm 2xl:text-base h-10 2xl:h-12"
          />
          {errors.website && (
            <p className="text-red-500 text-sm mt-1">
              {String(errors.website.message)}
            </p>
          )}
        </div>

        <div>
          <label className="block mb-1 text-sm 2xl:text-base">
            Number of Staff or Volunteers{" "}
            <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {staffOptions.map((option) => (
              <label
                key={option}
                className="flex items-center gap-2  cursor-pointer w-full"
              >
                <input
                  type="radio"
                  value={option}
                  {...register("staff_number")}
                  className="appearance-none w-5 h-5 border-2 border-blue-500 rounded-md checked:bg-blue-500 checked:border-blue-500 focus:outline-none transition-all duration-200 cursor-pointer"
                />
                <span className="text-gray-800 font-medium text-sm">
                  {option}
                </span>
              </label>
            ))}
          </div>
          {errors.staff_number && (
            <p className="text-red-500 text-sm mt-1">
              {String(errors.staff_number.message)}
            </p>
          )}
        </div>
      </SectionCard>

      <SectionCard title="Additional Information">
        <div>
          <label className="block mb-1 text-sm 2xl:text-base">
            What are you hoping THE ARK can help you with?{" "}
            <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 ">
            {needsOptions.map((option) => (
              <Checkbox
                key={option}
                label={option}
                value={option}
                checked={selectedNeeds.includes(option)}
                onChange={(e) => {
                  const checked = e.target.checked;
                  const updated = checked
                    ? [...selectedNeeds, option]
                    : selectedNeeds.filter((v) => v !== option);

                  setValue("needs", updated, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });

                  clearErrors(["needs", "other"]);
                  const other = (getValues("other") || "").trim();
                  if (updated.length === 0 && !other) {
                    const msg =
                      "Please select at least one need or specify Other.";
                    setError("needs", { type: "manual", message: msg });
                    setError("other", { type: "manual", message: msg });
                  }

                  trigger("needs_other_guard");
                }}
              />
            ))}
          </div>
        </div>

        <div>
          <label className="block mb-1 text-sm 2xl:text-base">
            Other (please specify)
          </label>
          <textarea
            {...register("other")}
            className="w-full border px-3 py-3 rounded text-xs sm:text-sm 2xl:text-base"
            placeholder="Tell us about other ways we can help"
            rows={1}
            maxLength={200}
            onChange={(e) => {
              const val = e.target.value;
              setValue("other", val, {
                shouldDirty: true,
                shouldValidate: true,
              });

              clearErrors(["needs", "other"]);
              const needs = getValues("needs") || [];
              const trimmed = val.trim();
              if (trimmed && trimmed.length < 10) {
                setError("other", {
                  type: "manual",
                  message: "Other must be at least 10 characters long.",
                });
              } else if (needs.length === 0 && !trimmed) {
                const msg = "Please select at least one need or specify Other.";
                setError("needs", { type: "manual", message: msg });
                setError("other", { type: "manual", message: msg });
              }

              trigger("needs_other_guard");
            }}
          />
          {(errors as any).needs_other_guard && (
            <p className="text-red-500 text-sm mt-1">
              {String((errors as any).needs_other_guard.message)}
            </p>
          )}
        </div>
      </SectionCard>

      <button
        type="submit"
        disabled={!isValid || isLoading}
        className="bg-blue-600 text-sm 2xl:text-base hover:bg-blue-700 text-white py-2 px-4 rounded w-full font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed"
      >
        {isLoading ? <Loader color={"#fff"} /> : "Submit"}
      </button>
    </form>
  );
};

const CompanyRegistrationForm: React.FC<Props> = ({
  onSubmit,
  companyName,
  goBack,
  isLoading,
  defaultValues,
  onChange,
}) => {
  const methods = useForm<FormValues>({
    defaultValues: defaultValues || { needs: [] },
    mode: "all",
    reValidateMode: "onChange",
  });

  useEffect(() => {
    if (!defaultValues) return;

    const currentDefaults = methods.getValues();
    if (JSON.stringify(currentDefaults) !== JSON.stringify(defaultValues)) {
      methods.reset(defaultValues, { keepErrors: true, keepDirty: true });
    }
  }, [defaultValues]);

  const values = methods.watch();
  const lastValues = useRef<FormValues | null>(null);

  useEffect(() => {
    if (!onChange) return;

    const current = values;

    // Only call if form data actually changed (deep compare)
    if (JSON.stringify(lastValues.current) !== JSON.stringify(current)) {
      lastValues.current = current;

      if (current.countryName && current.countryName.trim() !== "") {
        onChange(current);
      } else {
        const { countryName, country_id, ...rest } = current;
        onChange(rest);
      }
    }
  }, [values]);

  const handleSubmitForm = (data: FormValues) => onSubmit(data);

  return (
    <div className="bg-[#F9FAFB] min-h-screen flex flex-col">
      <div className="max-w-5xl mx-auto px-4 py-4 bg-[#F9FAFB] flex-col flex w-full">
        <div className="w-full flex justify-between">
          <div className="w-[5rem] cursor-pointer mt-8" onClick={goBack}>
            <img src="/back-arrow.svg" className="w-8 h-8" alt="" />
          </div>
          <div className="flex flex-col items-center mb-4 space-y-3 h-[5rem]">
            <img src="/logo_new.svg" alt="" />
          </div>
          <div className="w-[5rem]"></div>
        </div>

        <div className="flex-1 overflow-y-auto ">
          <h2 className="text-xl 2xl:text-2xl font-semibold text-center mb-2">
            Create account
          </h2>
          <p className="text-sm 2xl:text-base text-center text-gray-500 mb-4">
            We need a bit more information about your organisation.
          </p>
          <FormProvider {...methods}>
            <InnerForm onSubmit={handleSubmitForm} isLoading={isLoading} />
          </FormProvider>
        </div>
      </div>
    </div>
  );
};

export default CompanyRegistrationForm;
